import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { acknowledgeTrialNotice } from "@/api/auth";
import appLogo from "@/images/notanaicon.svg";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";

export function TrialNotice() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { subscription, checkSubscriptionStatus } = useAuth();
  const from = location.state?.from?.pathname || "/app/meetings";
  
  const handleContinue = async () => {
    setIsSubmitting(true);
    try {
      console.log("Acknowledging trial notice...");
      await acknowledgeTrialNotice();
      
      console.log("Forcing subscription status refresh...");
      const updatedStatus = await checkSubscriptionStatus(true); // Force refresh
      console.log("Updated subscription status:", updatedStatus);
      
      if (!updatedStatus.hasSeenTrialNotice) {
        throw new Error("Trial notice acknowledgment not reflected in subscription status");
      }
      
      console.log("Trial notice acknowledged successfully, navigating to:", from);
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Failed to acknowledge trial notice:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred. Please try again."
      });
      setIsSubmitting(false);
    }
    // Don't call setIsSubmitting(false) on success since we're navigating away
  };

  if (!subscription) {
    return <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner />
    </div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-4">
      {/* Animated Background Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950"></div>
        
        <div className="absolute top-0 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob" />
        <div className="absolute top-0 -right-40 w-96 h-96 bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-violet-400 to-purple-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-4000" />
      </div>

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-40 dark:opacity-20 pointer-events-none" />

      <Card className="w-full max-w-3xl relative z-10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src={appLogo} alt="Notana Logo" className="h-8 w-8 dark:invert" />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Welcome to Notana</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <h2 className="text-2xl font-semibold text-center">Your 30-Day Free Trial Starts Now!</h2>
          
          <div className="p-6 bg-muted rounded-lg">
            <p className="mb-4">Thank you for choosing Notana to streamline your meeting management! During your trial, enjoy:</p>
            
            <ul className="space-y-3 list-disc pl-5">
              <li>
                <strong>Full Access for 30 Days</strong>
                <p className="text-sm text-muted-foreground">Unlock all features to supercharge your meeting management.</p>
              </li>
              <li>
                <strong>Unlimited Meetings</strong>
                <p className="text-sm text-muted-foreground">Create and edit as many meetings and actions as you need.</p>
              </li>
              <li>
                <strong>Track Actions Easily</strong>
                <p className="text-sm text-muted-foreground">Manage actions from one centralized list across all meetings.</p>
              </li>
              <li>
                <strong>Seamless Collaboration</strong>
                <p className="text-sm text-muted-foreground">Share notes with colleagues and update action statuses together.</p>
              </li>
            </ul>
          </div>
          
          <div className="bg-primary/10 p-4 rounded-md">
            <h3 className="font-medium text-lg mb-2">What’s Next After Your Trial?</h3>
            <p className="mb-2">
              Your trial ends on <strong>{subscription.trialExpiresOn?.toLocaleDateString()}</strong>. You can then choose between:
            </p>
            <ul className="space-y-2 list-disc pl-5">
              <li><strong>Free Tier</strong> - View existing meetings and update shared action statuses—no new meetings.</li>
              <li><strong>£5/month Subscription</strong> - Keep full access, including unlimited meeting creation.</li>
            </ul>
            <p className="mt-3 text-sm text-muted-foreground">
              Try it risk-free with no payment details needed upfront. Pick a plan when your trial ends on <strong>{subscription.trialExpiresOn?.toLocaleDateString()}</strong>.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleContinue} disabled={isSubmitting} size="lg" className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40">
            {isSubmitting ? "Processing..." : "Get Started Now"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
